import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import Button1 from "../../form/Button1";
import getAdminTeamMembers from "../../../controllers/settings/adminTeam/getTeamMembers";
import { PriceBreakdown } from "./PriceBreakdown";
import { enqueueSnackbar } from "notistack";
import setRefundFee from "../../../controllers/booking/postTicketing/refund/setRefundFee";
import { Modal3 } from "../../DIsplay/Modal/Modal3";
import ExpiryInput from "./ExpiryInput";
import CommonTextAreaInput from "../../common/inputs/CommonTextAreaInput";
import { formatMoney } from "../../../features/utils/formatMoney";

export default function RefundQuote({ data, reload }) {
  const [handleOption, setHandleOption] = useState("direct");
  const [details, setDetails] = useState("");
  const [staffs, setStaffs] = useState([]);
  const [loadings, setLoadings] = useState({ fetchStaff: false });
  const [feeData, setFeeData] = useState({
    penality: data?.fee || 0,
    ticketDifference: data?.ticketDifference || 0,
  });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [paymentDeadline, setPaymentDeadline] = useState(
    moment().add(3, "hours").toISOString(),
  );

  useEffect(() => {
    if (data) loadStaff();
    // eslint-disable-next-line
  }, [data]);

  async function loadStaff() {
    setLoadings({ ...(loadings || {}), fetchStaff: true });
    const res = await getAdminTeamMembers();
    setLoadings({ ...(loadings || {}), fetchStaff: false });
    if (res.return) {
      setStaffs(res?.data?.data || []);
    }
  }

  async function handleSubmit() {
    const reqBody = {
      refundRequestId: data?._id,
      requestExpirationTime: paymentDeadline,
      passengerPrice: feeData?.passengerPrice?.filter(
        (obj, i, arr) =>
          arr?.findIndex((ob) => obj?.travelerType === ob?.travelerType) === i,
      ),
      remark: details,
    };

    setLoadings({ ...loadings, submit: true });
    const res = await setRefundFee(reqBody);
    setLoadings({ ...loadings, submit: false });

    if (res.return) {
      enqueueSnackbar("Quote Complete.", { variant: "success" });
      reload && reload();
    } else enqueueSnackbar(res.msg, { variant: "error" });
  }

  function handlePriceChange(next) {
    setFeeData(next);
  }

  const travellerMap = useMemo(
    () => ({
      ADT: "ADT",
      Adult: "ADT",
      CHD: "CHD",
      CNN: "CHD",
      Child: "CHD",
      INS: "INF",
      INF: "INF",
      Infant: "INF",
    }),
    [],
  );

  // ✅ show bottom buttons ONLY if each travelerType has at least one deducted amount > 0
  const canShowBottomActions = useMemo(() => {
    const arr = feeData?.passengerPrice || [];
    if (!arr.length) return false;

    const unique = arr.filter(
      (obj, i, a) =>
        a.findIndex((ob) => ob?.travelerType === obj?.travelerType) === i,
    );

    if (!unique.length) return false;

    return unique.every((p) => {
      const bd = p?.breakDown || [];
      return bd.some((x) => x?.isDeducted && Number(x?.amount || 0) > 0);
    });
  }, [feeData?.passengerPrice]);

  // ✅ TOTAL REFUND for all selected passengers (sum across traveler types * passengerCount)
  const totalRefundAllPassengers = useMemo(() => {
    const passengerIds = data?.passengerIds || [];
    const selectedTravellers = (data?.flightObj?.travelers || []).filter((t) =>
      passengerIds.includes(t?.id),
    );

    const travellersByTypeCount = selectedTravellers.reduce((acc, t) => {
      const normalized =
        travellerMap[t?.travelerType] || t?.travelerType || "ADT";
      acc[normalized] = (acc[normalized] || 0) + 1;
      return acc;
    }, {});

    const arr = feeData?.passengerPrice || [];
    if (!arr.length) return 0;

    const unique = arr.filter(
      (obj, i, a) =>
        a.findIndex((ob) => ob?.travelerType === obj?.travelerType) === i,
    );

    const parsePrice = (price) => {
      if (typeof price !== "string") return Number(price || 0);
      const numeric = parseFloat(
        price.replace(/^[A-Za-z\s]+/, "").replaceAll(",", ""),
      );
      return Number.isFinite(numeric) ? numeric : 0;
    };

    const travelerDict = {
      ADT: data?.flightObj?.adult || {},
      CHD: data?.flightObj?.child || {},
      INF: data?.flightObj?.infant || {},
    };

    const getUnitPrice = (travelerType, paxCount) => {
      const normalized = travellerMap[travelerType] || travelerType || "ADT";
      const pricing = travelerDict[normalized] || {};
      const total =
        parsePrice(pricing?.totalPrice) /
        (pricing?.passengerCount || paxCount || 1);
      // return paxCount > 0 ? total / paxCount : 0;
      return total;
    };

    return (
      unique.reduce((acc, p) => {
        const normalized =
          travellerMap[p?.travelerType] || p?.travelerType || "ADT";
        const paxCount = Number(travellersByTypeCount?.[normalized] || 0);
        if (!paxCount) return acc;

        const deductedPerPax = (p?.breakDown || []).reduce((sum, row) => {
          if (!row?.isDeducted) return sum;
          return sum + Number(row?.amount || 0);
        }, 0);

        const unitPrice = getUnitPrice(p?.travelerType, paxCount);
        const serviceFeePerPax = Number(p?.serviceFee ?? 0) || 0; // fallback if you ever store per-type; otherwise 0
        // NOTE: service fee per pax is coming from PriceBreakdown meta; if you don't store it in feeData, we won't double-count it here.

        const refundPerPax = Math.max(
          0,
          Number(unitPrice || 0) -
            Number(deductedPerPax || 0) -
            Number(serviceFeePerPax || 0),
        );

        return acc + refundPerPax * paxCount;
        // return acc + refundPerPax;
      }, 0) - data?.intraverseFee || 0
    );
  }, [data, feeData?.passengerPrice, travellerMap]);

  return (
    <div className="flex flex-col gap-4 max-h-[80vh] overflow-scroll scroll-hide">
      <PriceBreakdown data={data} callback={handlePriceChange} />
      <ExpiryInput callback={(d) => setPaymentDeadline(d)} />
      <CommonTextAreaInput
        label="Remarks"
        value={details}
        onChange={setDetails}
      />

      <Modal3
        open={openConfirm}
        setOpen={() => setOpenConfirm(false)}
        title="Confirm Quote"
      >
        <div className="flex flex-col gap-4 p-10">
          <div className="text-lg pb-4">
            Are you sure you want to confirm this quote?
          </div>
          <div className="flex gap-4">
            <div>
              <Button1 onClick={() => setOpenConfirm(false)} variant="outlined">
                Cancel
              </Button1>
            </div>
            <Button1 loading={loadings?.submit} onClick={handleSubmit}>
              Confirm
            </Button1>
          </div>
        </div>
      </Modal3>

      {canShowBottomActions ? (
        <div className="sticky bottom-0 -mx-4 mt-2 border-t border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            {/* Left: total refund */}
            <div className="flex flex-col">
              <div className="text-xs text-gray-500">Total Refund</div>
              <div className="text-base font-semibold text-gray-900">
                {formatMoney(totalRefundAllPassengers)}
              </div>
            </div>

            {/* Right: actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setOpenConfirm(false)}
                className="
                  h-10 rounded-lg px-4
                  border border-gray-400
                  text-sm font-medium text-gray-700 hover:text-gray-900
                "
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={loadings?.submit}
                onClick={() => setOpenConfirm(true)}
                className={`
                  h-10 rounded-lg px-5
                  text-sm font-semibold text-white
                  ${
                    loadings?.submit
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-primary1 hover:opacity-90"
                  }
                `}
              >
                Confirm quote
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
